import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, Menu, X, Plus, User as UserIcon, LogOut, ShieldCheck, MessageSquare, CalendarDays, Heart, BarChart3 } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { useApp } from "@/store";

export default function Navbar() {
  const { t, lang, setLang, currency, setCurrency, user, logout } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };

  const Switchers = () => (
    <div className="flex items-center gap-2">
      <div className="flex items-center rounded-full border border-gray-200 p-0.5" data-testid="lang-switcher">
        {["en", "pt", "es", "fr"].map((l) => (
          <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
            className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase transition-colors ${lang === l ? "bg-[#002FA7] text-white" : "text-gray-500 hover:text-[#002FA7]"}`}>
            {l}
          </button>
        ))}
      </div>
      <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="currency-switcher"
        className="rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-bold text-gray-700 outline-none transition-colors hover:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]">
        <option value="USD">USD</option>
        <option value="BRL">BRL</option>
        <option value="AUD">AUD</option>
        <option value="GBP">GBP</option>
        <option value="EUR">EUR</option>
        <option value="CAD">CAD</option>
      </select>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 font-display text-xl font-extrabold tracking-tight">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#002FA7] text-white"><Globe size={18} /></span>
          RentGlobe
        </Link>

        <div className="hidden items-center gap-6 whitespace-nowrap md:flex">
          <Link to="/" data-testid="nav-browse" className="text-sm font-medium text-gray-600 transition-colors hover:text-[#002FA7]">{t("nav_browse")}</Link>
          <Link to="/how-it-works" data-testid="nav-how" className="text-sm font-medium text-gray-600 transition-colors hover:text-[#002FA7]">{t("nav_how")}</Link>
          <Link to="/for-businesses" data-testid="nav-business" className="text-sm font-medium text-gray-600 transition-colors hover:text-[#002FA7]">{t("nav_business")}</Link>
          <Link to="/pricing" data-testid="nav-pricing" className="hidden text-sm font-medium text-gray-600 transition-colors hover:text-[#002FA7] lg:inline">{t("nav_pricing")}</Link>
          <Switchers />
          {user ? (
            <div className="flex items-center gap-2">
              <Link to="/create" data-testid="nav-list-item" className="inline-flex items-center gap-1.5 rounded-full bg-[#FF3B30] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E6352B]">
                <Plus size={16} /> {t("nav_list_item")}
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]" title="Admin Dashboard"><ShieldCheck size={17} /></Link>
              )}
              <NotificationBell />
              <Link to="/saved" data-testid="nav-saved" className="grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-[#FF3B30] hover:text-[#FF3B30]" title="Saved"><Heart size={17} /></Link>
              <Link to="/dashboard" data-testid="nav-dashboard" className="grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]" title="Owner dashboard"><BarChart3 size={17} /></Link>
              <Link to="/bookings" data-testid="nav-bookings" className="grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]" title={t("my_bookings")}><CalendarDays size={17} /></Link>
              <Link to="/messages" data-testid="nav-messages" className="grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]" title={t("nav_messages")}><MessageSquare size={17} /></Link>
              <Link to="/profile" data-testid="nav-profile" className="grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]"><UserIcon size={17} /></Link>
              <button onClick={doLogout} data-testid="nav-logout" className="grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-red-400 hover:text-red-500"><LogOut size={17} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" data-testid="nav-login" className="rounded-full px-4 py-2 text-sm font-bold text-[#002FA7] transition-colors hover:bg-[#002FA7]/5">{t("nav_login")}</Link>
              <Link to="/register" data-testid="nav-register" className="rounded-full bg-[#002FA7] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#002280]">{t("nav_register")}</Link>
            </div>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>

      {open && (
        <div className="space-y-3 border-t border-gray-100 px-5 py-4 md:hidden" data-testid="mobile-menu">
          <Switchers />
          <Link to="/" onClick={() => setOpen(false)} className="block text-sm font-medium">{t("nav_browse")}</Link>
          <Link to="/how-it-works" onClick={() => setOpen(false)} className="block text-sm font-medium">{t("nav_how")}</Link>
          <Link to="/for-businesses" onClick={() => setOpen(false)} className="block text-sm font-medium">{t("nav_business")}</Link>
          {user ? (
            <>
              <Link to="/create" onClick={() => setOpen(false)} className="block text-sm font-bold text-[#FF3B30]">{t("nav_list_item")}</Link>
              <Link to="/bookings" onClick={() => setOpen(false)} className="block text-sm font-medium">{t("my_bookings")}</Link>
              <Link to="/saved" onClick={() => setOpen(false)} className="block text-sm font-medium">Saved items</Link>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="block text-sm font-medium">Owner dashboard</Link>
              <Link to="/messages" onClick={() => setOpen(false)} className="block text-sm font-medium">{t("nav_messages")}</Link>
              <Link to="/profile" onClick={() => setOpen(false)} className="block text-sm font-medium">{t("nav_profile")}</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block text-sm font-medium">Admin Dashboard</Link>}
              <button onClick={doLogout} className="block text-sm font-medium text-red-500">{t("nav_logout")}</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="block text-sm font-bold text-[#002FA7]">{t("nav_login")}</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="block text-sm font-bold">{t("nav_register")}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
